/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 인용 블록을 정의한다.
 *
 * @file /src/rootin/blocks/quote/Quote.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 7. 26.
 */
import { Block } from '@rootin/base/Block';
import { Element } from '@rootin/base/Element';
import { Editor } from '@rootin/core/Editor';
import { Text } from '@rootin/elements/text/Text';
import { Html } from '@utils/Html';
import { Language, LanguageText } from '@utils/Language';
import './Quote.css';

export class Quote extends Block {
    /**
     * 블록타입
     */
    static type = 'quote';

    /**
     * 블록종류
     */
    static kind: 'inline' | 'atomic' = 'atomic';

    /**
     * 블록아이콘
     */
    static icon: string =
        '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-quote-icon lucide-quote"><path d="M16 3a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2 1 1 0 0 1 1 1v1a2 2 0 0 1-2 2 1 1 0 0 0-1 1v2a1 1 0 0 0 1 1 6 6 0 0 0 6-6V5a2 2 0 0 0-2-2z"/><path d="M5 3a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2 1 1 0 0 1 1 1v1a2 2 0 0 1-2 2 1 1 0 0 0-1 1v2a1 1 0 0 0 1 1 6 6 0 0 0 6-6V5a2 2 0 0 0-2-2z"/></svg>';

    /**
     * 블록명
     */
    static label: LanguageText = Language.get('rootin.blocks.quote.label');

    /**
     * 인용문 엘리먼트
     */
    quote: Text;

    /**
     * 출처 엘리먼트
     */
    reference: Text;

    /**
     * 인용 블록을 정의한다.
     *
     * @param editor - 이 블록이 속한 에디터
     */
    constructor(editor: Editor) {
        super(editor);
        this.quote = new Text(this, {
            name: 'quote',
            placeholder: Language.get('rootin.blocks.quote.quote.placeholder'),
        });
        this.$getBlock().append(this.quote.$getElement());

        this.$getBlock().append(
            Html.create('div', { 'contenteditable': 'false' }, '나는 편집이 되면 안되는 블록 디자인적요소야.')
        );

        this.reference = new Text(this, {
            name: 'reference',
            placeholder: Language.get('rootin.blocks.quote.reference.placeholder'),
        });
        this.$getBlock().append(this.reference.$getElement());
    }

    /**
     * 블록을 렌더링한다.
     */
    async render(): Promise<void> {
        await this.quote.render();
        await this.reference.render();
    }

    /**
     * 블록이 비었는지 확인한다.
     *
     * @returns 블록이 비었는지 여부
     */
    isEmpty(): boolean {
        return this.quote.isEmpty() && this.reference.isEmpty();
    }

    /**
     * 블록값을 가져온다.
     * element 변수가 존재할 경우, 해당 엘리먼트의 기본값을 가져온다.
     *
     * @param element - 기본값을 가져오려는 엘리먼트 (NULL 인 경우 전체 블록데이터)
     * @returns 블록값
     */
    getValue(element: Element = null): any {
        if (element !== null) {
            return this.defaultValue?.[element.name] ?? null;
        }

        return {
            id: this.getId(),
            type: this.getType(),
            quote: this.quote.getValue(),
            reference: this.reference.getValue(),
        };
    }

    /**
     * 블록값을 지정한다.
     *
     * @param value - 블록값
     * @param is_default_value - 기본값 여부
     */
    async setValue(value: { [key: string]: any }, is_default_value: boolean = false): Promise<void> {
        if (is_default_value === true) {
            this.defaultValue = value;
        }

        await this.quote.setValue(value.quote);
        await this.reference.setValue(value.reference);
    }

    /**
     * 블록이 삽입되었을 때 이벤트를 처리한다.
     */
    async onInserted(): Promise<void> {
        this.quote.focus();
    }

    /**
     * 블록내에서 Enter 키입력이 발생했을 때 이벤트를 처리한다.
     *
     * @param element - 실제로 Enter 키입력이 발생한 엘리먼트 (NULL 이면 블록자체)
     */
    onEnter(element: Element): void {
        if (element.name == 'quote') {
            this.editor.caret.insertLineBreak();
            return;
        }

        const container = this.getParent();
        if (container.blocks.includes('paragraph') === false) {
            return;
        }

        const ParagraphClass = this.getBlockClass('paragraph');
        if (ParagraphClass === null) {
            return;
        }

        container.splitBlock(ParagraphClass.create(this.editor), this, element, 'text');
    }
}
